import React from 'react';
import { useTranslation } from 'react-i18next';
import { Building, Settings as Gears, Layers } from '../icons/s2';
import { BackButton } from '../common';
import { useResizablePanel } from '../../hooks/useResizablePanel';
import { useIsMobile } from '../../hooks/useIsMobile';
import '../ChatBox/AppSidebar.css';

// Org Details, Default Org Config, Models (Registry + Configurations tabs,
// merged from the two standalone destinations design-frames/models.html
// originally showed). "Default Domain Config" was a nav-unreachable page
// (deliberately never linked from this sidebar) and has since been removed
// entirely, per design-frames/01-organizations-list.html.
export type CommandCenterView =
  | 'organizations'
  | 'defaultOrgConfig'
  | 'models'
  | null;

export interface CommandCenterSidebarProps {
  activeView: CommandCenterView;
  onBack: () => void;
  onOrganizations: () => void;
  onDefaultOrgConfig: () => void;
  onModels: () => void;
  /** Whether the mobile off-canvas drawer is currently open (mirrors
   *  AppSidebar's `isDrawerOpen`). When closed on mobile the sidebar is
   *  translated off-screen, so it is marked `inert` to keep it out of the tab
   *  order and the accessibility tree. */
  isDrawerOpen?: boolean;
  /** Closes the mobile off-canvas drawer after a terminal nav choice. */
  onMobileNavClose?: () => void;
}

export const CommandCenterSidebar: React.FC<CommandCenterSidebarProps> = ({
  activeView,
  onBack,
  onOrganizations,
  onDefaultOrgConfig,
  onModels,
  isDrawerOpen = false,
  onMobileNavClose,
}) => {
  const { width: sidebarWidth, isDragging, onResizeStart: handleResizeStart } = useResizablePanel({
    minWidth: 200, maxWidth: 420, defaultWidth: 220, direction: 'right',
  });
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Each row here navigates straight to a page, so it must close the mobile
  // drawer itself — App.tsx's "close on activeView change" is a no-op when the
  // tapped row is already the active view (re-tapping), leaving the drawer stuck.
  const closeDrawerOnMobile = React.useCallback(() => {
    if (isMobile) onMobileNavClose?.();
  }, [isMobile, onMobileNavClose]);

  const isInCommandCenter = true;

  const getPanelTransform = (direction: 'left' | 'right', visible: boolean) => {
    if (visible) return 'translateX(0)';
    return direction === 'left' ? 'translateX(-100%)' : 'translateX(100%)';
  };

  return (
    <div className="sidebar-area" inert={isMobile && !isDrawerOpen}>
      <div
        className="sidebar-wrapper"
        style={isMobile ? undefined : { width: sidebarWidth }}
      >
        <div className="sidebar-container">
          <div
            className="sidebar-panel sidebar-panel--nav"
            style={{
              transform: getPanelTransform('right', isInCommandCenter),
              opacity: isInCommandCenter ? 1 : 0,
              pointerEvents: isInCommandCenter ? 'auto' : 'none',
            }}
          >
            <div className="workspace-header">
              <BackButton
                onPress={onBack}
                aria-label={t('commandCenter.sidebar.exitCommandCenter')}
              />
              <div className="workspace-title" title={t('commandCenter.sidebar.commandCenterTitle')}>
                {t('commandCenter.sidebar.commandCenter')}
              </div>
            </div>

            <div className="actions-list">
              <button
                type="button"
                className={`action-row ${activeView === 'organizations' ? 'action-row--active' : ''}`}
                onClick={() => { onOrganizations(); closeDrawerOnMobile(); }}
                aria-label={t('commandCenter.sidebar.organizationDetails')}
              >
                <span className="action-icon-wrap">
                  <Building size="S" />
                </span>
                {t('commandCenter.sidebar.organizations')}
              </button>

              <button
                type="button"
                className={`action-row ${activeView === 'defaultOrgConfig' ? 'action-row--active' : ''}`}
                onClick={() => { onDefaultOrgConfig(); closeDrawerOnMobile(); }}
                aria-label={t('commandCenter.sidebar.defaultOrgConfig')}
              >
                <span className="action-icon-wrap">
                  <Gears size="S" />
                </span>
                {t('commandCenter.sidebar.globalConfig')}
              </button>

              <button
                type="button"
                className={`action-row ${activeView === 'models' ? 'action-row--active' : ''}`}
                onClick={() => { onModels(); closeDrawerOnMobile(); }}
                aria-label={t('commandCenter.sidebar.models')}
              >
                <span className="action-icon-wrap">
                  <Layers size="S" />
                </span>
                {t('commandCenter.sidebar.models')}
              </button>
            </div>
          </div>
        </div>
        <div
          className={`resize-handle ${isDragging ? 'resize-handle--dragging' : ''}`}
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
        />
      </div>
    </div>
  );
};

export default CommandCenterSidebar;
